import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import * as Cookies from 'js-cookie';
import {EMPTY, Subject} from 'rxjs';
import {environment} from 'src/environments/environment';
import {ConstantsService, ConstantsService as constants} from '../constants/constants.service';
import {RegionService} from '../region/region.service';
import {Init} from "../../app.init";
import {PermissionsService} from "../permissions/permissions.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authUrl = `${environment.AUTH_API}`;
  url = new Init().makeRegionalApiUrl(environment.use_global_id ? Cookies.get(ConstantsService.API_REGION_TOKEN) : Cookies.get(ConstantsService.DEPRECATED_API_REGION_TOKEN));
  unauthenticated$ = new Subject<string>();
  loggingOut = false;
  userUid = '';
  decodeToken: any = {};

  private httpClient: HttpClient;
  constructor(
    private jwtHelper: JwtHelperService,
    private region: RegionService,
    private permissions: PermissionsService,
    handler: HttpBackend
  ) {
    this.authUrl = this.region.getRegionalUrl(this.authUrl);
    this.httpClient = new HttpClient(handler);
  }


  logoutByUser() {
    const headers = new HttpHeaders()
    .set('Authorization', `Bearer ${this.getAuthToken()}`);
    this.httpClient.post(`${this.authUrl}logout/`, {},{ headers: headers}).subscribe(() => {
      this.logout();
    });
  }

  logout(): void {
    // Remove token
    this.permissions.isGlobalFeatureActivated('ENT-9105-PENETRATION-TEST-2025') ? this.removeToken() :
      this.deprecatedRemoveToken();

    // Reset all filters and search when logging out
    localStorage.removeItem("backGroundCheck_filters");
    localStorage.removeItem("backGroundCheck_search");
    localStorage.removeItem("exit_filters");
    localStorage.removeItem("exit_search");
    localStorage.removeItem("pulse_filters");
    localStorage.removeItem("pulse_search");
    localStorage.removeItem("reference_filters");
    localStorage.removeItem("reference_search");
    localStorage.removeItem("requests-filter");

    // Refresh page to ensure user get latest version of app
    if (!this.loggingOut) this.redirectToLogin();

    this.loggingOut = true;
  }

  redirectToLogin() {
    location.assign(`${environment.LOGIN_URL}?retUrl=${encodeURIComponent(location.href)}`);
  }

  deprecatedRemoveToken() {
    if (environment.use_global_id) {
      Cookies.remove(`${ConstantsService.AUTH_TOKEN}`, {domain: environment.COOKIE_DOMAIN});
      Cookies.remove(`${ConstantsService.REFRESH_TOKEN}`, {domain: environment.COOKIE_DOMAIN});
    } else {
      Cookies.remove(`${ConstantsService.DEPRECATED_AUTH_TOKEN}`, {domain: environment.COOKIE_DOMAIN});
      Cookies.remove(`${ConstantsService.DEPRECATED_REFRESH_TOKEN}`, {domain: environment.COOKIE_DOMAIN});
    }
    Cookies.remove(`${ConstantsService.XREF_PLATFORM}`, {domain: environment.COOKIE_DOMAIN});
    this.unauthenticated$.next("undefined");
  }

  removeToken() {
    sessionStorage.removeItem(`${ConstantsService.AUTH_TOKEN}`);
    Cookies.remove(`${ConstantsService.XREF_ID}`, {domain: environment.COOKIE_DOMAIN});
    Cookies.remove(`${ConstantsService.XREF_PLATFORM}`, {domain: environment.COOKIE_DOMAIN});
    this.unauthenticated$.next("undefined");
  }

  tokenNotExpired(tokenName: string, jwt?: string) {
    const token: string|undefined|null = jwt || Cookies.get(tokenName) ||
      (this.permissions.isGlobalFeatureActivated('ENT-9105-PENETRATION-TEST-2025') ? sessionStorage.getItem(tokenName) : null);
    return token != null && !this.jwtHelper.isTokenExpired(token);
  }

  loggedIn() {
    return environment.use_global_id ? Cookies.get(constants.AUTH_TOKEN) != undefined : Cookies.get(constants.DEPRECATED_AUTH_TOKEN) != undefined;
  }

  getUserUid() {
    const token = environment.use_global_id ? Cookies.get(constants.AUTH_TOKEN)! : Cookies.get(constants.DEPRECATED_AUTH_TOKEN)!;
    this.decodeToken = this.jwtHelper.decodeToken(token);
    this.userUid = this.decodeToken['user_uid'];
  }

  getAuthToken() {
    if(!this.loggedIn()) {
      // Clear all cookies and redirect to login
      this.logout();
    }

    let token = environment.use_global_id ? Cookies.get(ConstantsService.AUTH_TOKEN)! : Cookies.get(ConstantsService.DEPRECATED_AUTH_TOKEN)!;
    // If token is expired, refresh token

    if (this.jwtHelper.isTokenExpired(token)) {
      const apiRegion = environment.use_global_id ? Cookies.get(ConstantsService.API_REGION_TOKEN) : Cookies.get(ConstantsService.DEPRECATED_API_REGION_TOKEN);
      let selectedOrgUid = Cookies.get(constants.ORG_COOKIE);
      this.refreshToken(selectedOrgUid, apiRegion);
      // Get new token
      if (environment.use_global_id) {
        token = Cookies.get(ConstantsService.AUTH_TOKEN) || "";
      } else {
        token = Cookies.get(ConstantsService.DEPRECATED_AUTH_TOKEN) || "";
      }
    }
    // If token not available, logout user and terminate request
    if (token == null) {
      this.logout();
      return EMPTY;
    }

    return token;
  }

  refreshToken(org_uid: any, region: any, callback?: any) {
    if ((
      environment.use_global_id && Cookies.get(constants.REFRESH_TOKEN) !== undefined && Cookies.get(constants.AUTH_TOKEN) !== undefined
    ) || (
      !environment.use_global_id && Cookies.get(constants.DEPRECATED_REFRESH_TOKEN) !== undefined && Cookies.get(constants.DEPRECATED_AUTH_TOKEN) !== undefined
    )) {
      const req = new XMLHttpRequest();

      req.open('POST', `${this.url}auth/refresh`, false);
      req.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');
      req.setRequestHeader('Accept', 'application/json');
      req.onload = function () {
        if (req.status === 200) {
          // Set new token as cookie
          const data = JSON.parse(req.response);
          if (environment.use_global_id) {
            Cookies.set(constants.REFRESH_TOKEN, data['refresh'], {domain: environment.COOKIE_DOMAIN});
            Cookies.set(constants.AUTH_TOKEN, data['access'], {domain: environment.COOKIE_DOMAIN});
            Cookies.set(constants.API_REGION_TOKEN, data['region'], {domain: environment.COOKIE_DOMAIN});
          } else {
            Cookies.set(constants.DEPRECATED_REFRESH_TOKEN, data['refresh'], {domain: environment.COOKIE_DOMAIN});
            Cookies.set(constants.DEPRECATED_AUTH_TOKEN, data['access'], {domain: environment.COOKIE_DOMAIN});
            Cookies.set(constants.DEPRECATED_API_REGION_TOKEN, data['region'], {domain: environment.COOKIE_DOMAIN});
          }
          Cookies.set(constants.ORG_COOKIE, data['organisation'], {domain: environment.COOKIE_DOMAIN});
          if(callback) callback();
        } else if (req.status === 400) {
          if(callback) callback(JSON.parse(req.response));
        } else {
          if(callback) callback(new Error());
        }
      };
      req['data'] = {
        'refresh': environment.use_global_id ? Cookies.get(constants.REFRESH_TOKEN) : Cookies.get(constants.DEPRECATED_REFRESH_TOKEN),
        'uuid': org_uid,
        'region': region
      }
      req.send(JSON.stringify(req['data']));
    }
  }
}
